import {react, useState, useEffect, useContext} from 'react';
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import NewContactMethodForm from '../forms/newContactMethod.js';
import DeleteModal from '../modals/delete.js';
import getAPI from '../functions/API/getAPI.js'
import postAPI from '../functions/API/postAPI.js'
import GlobalContext from "../context/GlobalContext.js";
import SlideOverRight from '../components/slideOverRight.js';
import Loader from '../components/loader.js'
import {Heading, Subheading} from '../components/heading'
import {Button} from '../components/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table.jsx'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../components/dialog'
import { Field, Label } from '../components/fieldset'
import { Input } from '../components/input'
import { Listbox, ListboxLabel, ListboxOption } from '../components/listbox'
import { Strong, Text, TextLink, Code } from '../components/text'
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../components/description-list'
import { Badge } from '../components/badge'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../components/dropdown'
import { EllipsisHorizontalIcon, InformationCircleIcon } from '@heroicons/react/16/solid'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip'
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../components/alert'

export default function CredentialManager() {
    const {settings} = useContext(GlobalContext)
    let [isOpen, setIsOpen] = useState(false)
    let [selectedCredentialType, setSelectedCredentialType] = useState('')
    let [credentials, setCredentials] = useState([])
    let [entraCredentials, setEntraCredentials] = useState({})
    let [editedCredential, setEditedCredential] = useState({})
    let [editIsOpen, setEditIsOpen] = useState(false)
    let [deleteIsOpen, setDeleteIsOpen] = useState(false)
    let [credentialToDelete, setCredentialToDelete] = useState({})
    let [disconnectIsOpen, setDisconnectIsOpen] = useState(false)
    let [credentialToDisconnect, setCredentialToDisconnect] = useState({})
    let [connectingId, setConnectingId] = useState(null)
    let [requiredPermissions, setRequiredPermissions] = useState(null)

    async function GetCredentials(){
        const CredentialsResponse = await getAPI('admin/entra/registrations');
        if (CredentialsResponse.response.ok) {
            setCredentials(CredentialsResponse.data)
        } else {
            toast.error('Unable to get available credentials');
        }
    }

    async function SaveCredential(){
        if(!entraCredentials.name || !entraCredentials.tenant_id){
            toast.error('Please fill in all required fields');
            return;
        }

        const authType = selectedCredentialType || 'platform_app';

        // For legacy credentials, require client_id and secret
        if(authType === 'legacy' && (!entraCredentials.client_id || !entraCredentials.secret)){
            toast.error('Please fill in all required fields including Client ID and Secret');
            return;
        }

        const payload = {
            name: entraCredentials.name,
            tenant_id: entraCredentials.tenant_id,
            auth_type: authType
        }

        // Add client credentials for legacy type
        if(authType === 'legacy'){
            payload.client_id = entraCredentials.client_id;
            payload.secret = entraCredentials.secret;
        }

        const CredentialResponse = await postAPI('admin/entra/registration', payload)
        if (CredentialResponse.response.ok) {
            setIsOpen(false);
            setSelectedCredentialType('');
            setEntraCredentials({});
            GetCredentials();
            // Only initiate admin consent for platform_app credentials
            if(authType === 'platform_app'){
                initiateAdminConsent(CredentialResponse.data);
            } else {
                toast.success('Credential saved successfully.');
            }
        } else {
            toast.error('Something went wrong, please try again');
        }
    }

    // Open Microsoft admin consent popup
    async function initiateAdminConsent(credential) {
        setConnectingId(credential.id);
        const response = await getAPI(`admin/entra/registration/${credential.id}/authorize`);
        if (!response.response.ok) {
            toast.error('Failed to get authorization URL');
            setConnectingId(null);
            return;
        }

        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        const popup = window.open(response.data.authorization_url, 'entra-consent', `width=${width},height=${height},left=${left},top=${top}`);

        // Listen for callback message
        const messageHandler = async (event) => {
            if (event.data.type === 'ENTRA_ADMIN_CONSENT_CODE') {
                window.removeEventListener('message', messageHandler);

                // Call backend to confirm consent
                const callbackResponse = await postAPI('admin/entra/callback', {
                    credential_id: event.data.credential_id,
                    admin_consent: event.data.admin_consent
                });

                setConnectingId(null);
                if (callbackResponse.response.ok) {
                    toast.success('Admin consent granted successfully!');
                    GetCredentials();
                } else {
                    toast.error('Failed to confirm admin consent');
                }
            } else if (event.data.type === 'ENTRA_ADMIN_CONSENT_COMPLETE') {
                window.removeEventListener('message', messageHandler);
                setConnectingId(null);
                if (!event.data.success) {
                    toast.error(event.data.error || 'Admin consent failed');
                }
            }
        };
        window.addEventListener('message', messageHandler);

        // Check if popup closed without completing
        const checkClosed = setInterval(() => {
            if (popup.closed) {
                clearInterval(checkClosed);
                window.removeEventListener('message', messageHandler);
                setConnectingId(null);
            }
        }, 1000);
    }

    // Disconnect credential
    async function disconnectEntra() {
        if (!credentialToDisconnect.id) {
            toast.error('No credential selected');
            return;
        }
        const response = await postAPI(`admin/entra/registration/${credentialToDisconnect.id}/disconnect`);
        if (response.response.ok) {
            setDisconnectIsOpen(false);
            setCredentialToDisconnect({});
            toast.success('Credential disconnected');
            GetCredentials();
        } else {
            toast.error('Failed to disconnect');
        }
    }

    function editCredential(credential){
        setEditedCredential({...credential, name: credential.name, id: credential.id})
        setEditIsOpen(true)
    }

    async function UpdateCredential(){
        if(editedCredential.id === ''){
            toast.error('No change detected');
            return;
        }

        const CredentialResponse = await postAPI('admin/entra/registration/update', editedCredential)
        if (CredentialResponse.response.ok) {
            setEditIsOpen(false);
            setEditedCredential({});
            GetCredentials();
            toast.success('Credential updated successfully.');
        } else {
            toast.error('Something went wrong, please try again');
        }
    }

    async function DeleteCredential(){
        if(credentialToDelete.id === ''){
            toast.error('No id found');
            return;
        }
        const CredentialResponse = await postAPI('admin/entra/registration/delete/'+credentialToDelete.id)
        if(CredentialResponse.response.status === 400){
            setDeleteIsOpen(false);
            toast.error('Credential currently in use.');
            return;
        }

        if (CredentialResponse.response.ok) {
            setDeleteIsOpen(false);
            setCredentialToDelete({});
            GetCredentials();
            toast.success('Credential deleted successfully.');
        } else {
            toast.error('Something went wrong, please try again');
        }
    }

    function CredsTable() {
        return (
            <Table>
                <TableHead>
                <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader className="relative w-12">
                        <span className="sr-only">Actions</span>
                    </TableHeader>
                </TableRow>
                </TableHead>
                <TableBody>
                    {credentials && credentials.map((credential, index) => (
                    <TableRow key={index}>
                        <TableCell className="font-medium">{credential.name}</TableCell>
                        <TableCell>
                            <Code>Microsoft Entra ID</Code>
                            {credential.auth_type === 'platform_app' ? (
                                <Badge color="blue" className="ml-2">OAuth 2.0</Badge>
                            ) : (
                                <Badge color="zinc" className="ml-2">Manual</Badge>
                            )}
                        </TableCell>
                        <TableCell>
                            {connectingId === credential.id ? (
                                <Badge color="blue" className="inline-flex items-center gap-1.5">
                                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Connecting...
                                </Badge>
                            ) : (!credential.auth_type || credential.auth_type === 'legacy') ? (
                                <Badge color="zinc">Manual Config</Badge>
                            ) : credential.needs_reconsent ? (
                                <Badge color="orange">Update Required</Badge>
                            ) : credential.connected ? (
                                <Badge color="green">Connected</Badge>
                            ) : (
                                <Badge color="amber">Consent Required</Badge>
                            )}
                        </TableCell>
                        <TableCell>
                            <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                                <Dropdown>
                                <DropdownButton plain aria-label="More options">
                                    <EllipsisHorizontalIcon />
                                </DropdownButton>
                                <DropdownMenu anchor="bottom end">
                                    <DropdownItem onClick={()=>{editCredential(credential)}}>Edit</DropdownItem>
                                    {credential.auth_type === 'platform_app' && !credential.connected && (
                                        <DropdownItem onClick={()=>{initiateAdminConsent(credential)}}>
                                            Grant Admin Consent
                                        </DropdownItem>
                                    )}
                                    {credential.auth_type === 'platform_app' && credential.needs_reconsent && (
                                        <DropdownItem onClick={()=>{initiateAdminConsent(credential)}}>
                                            Update Permissions
                                        </DropdownItem>
                                    )}
                                    {credential.auth_type === 'platform_app' && credential.connected && !credential.needs_reconsent && (
                                        <DropdownItem onClick={()=>{setCredentialToDisconnect(credential); setDisconnectIsOpen(true)}}>
                                            Disconnect
                                        </DropdownItem>
                                    )}
                                    <DropdownItem onClick={()=>{setCredentialToDelete(credential), setDeleteIsOpen(true)}}>Delete</DropdownItem>
                                </DropdownMenu>
                                </Dropdown>
                            </div>
                        </TableCell>
                    </TableRow>
                    ))}
                </TableBody>
            </Table>
        )
    }

    useEffect(() => {
        async function fetchPermissions() {
            const response = await getAPI('admin/entra/permissions');
            if (response.response.ok) {
                setRequiredPermissions(response.data);
            }
        }
        fetchPermissions();
        GetCredentials();
    }, [])  

    return (
        <>
            <AdminSidebar>
                <div className="flex w-full flex-wrap items-end justify-between gap-4 border-zinc-950/10 pb-6 dark:border-white/10">
                    <div>
                        <Heading>Credential Manager</Heading>
                        <Text>
                            Credential Manager is designed to allow you to manager all of your integration credentials in one place
                        </Text>
                    </div>
                    <div className="flex gap-4">
                        <Button color='blue' onClick={() => setIsOpen(true)}>New Credential</Button>
                    </div>
                </div>
                
                <CredsTable/>
                
                <Dialog open={isOpen} onClose={(()=>{setIsOpen(false), setSelectedCredentialType(''), setEntraCredentials({})})}>
                    <DialogTitle>New Credential</DialogTitle>
                    <DialogDescription>
                        Connect your Microsoft Entra tenant to enable SharePoint and other Microsoft services.
                    </DialogDescription>
                    <DialogBody>
                        <Field>
                            <div className="flex items-center gap-1.5">
                                <Label>Setup Method</Label>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <InformationCircleIcon className="h-4 w-4 text-zinc-400 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                            <p className="font-medium mb-1">OAuth 2.0 (Recommended)</p>
                                            <p className="text-xs mb-2">Grant admin consent to our platform app. We securely request tokens on your behalf using your tenant ID - no credentials to manage or rotate.</p>
                                            <p className="font-medium mb-1">Manual Configuration</p>
                                            <p className="text-xs">Create your own app registration in Azure and enter the credentials here. You are responsible for managing and rotating secrets.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Listbox name="credential-type" value={selectedCredentialType || 'platform_app'} onChange={(value) => setSelectedCredentialType(value)}>
                                <ListboxOption value="platform_app">
                                    <ListboxLabel>OAuth 2.0 (Recommended)</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value="legacy">
                                    <ListboxLabel>Manual Configuration</ListboxLabel>
                                </ListboxOption>
                            </Listbox>
                            <Text className="mt-1 text-sm text-zinc-500">
                                {selectedCredentialType === 'legacy'
                                    ? 'Manually enter your own Azure app registration credentials. You manage secret rotation.'
                                    : 'Quick setup - just enter your tenant ID and grant admin consent. No secrets to manage.'}
                            </Text>
                        </Field>

                        <Field>
                            <Label>Credential Name</Label>
                            <Input name="credential-name" placeholder="e.g., Production SharePoint" onChange={(e) => { setEntraCredentials({ ...entraCredentials, name: e.target.value }) }} />
                        </Field>

                        <Field>
                            <div className="flex items-center gap-1.5">
                                <Label>Microsoft Entra Tenant ID</Label>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <InformationCircleIcon className="h-4 w-4 text-zinc-400 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p className="font-medium mb-1">Where to find your Tenant ID</p>
                                            <p className="text-xs">Azure Portal → Microsoft Entra ID → Overview → Tenant ID</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Input autoComplete="new-tenantid" name="tenant-id" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" onChange={(e) => { setEntraCredentials({ ...entraCredentials, tenant_id: e.target.value }) }}/>
                        </Field>

                        {selectedCredentialType === 'legacy' && (
                            <>
                                <Field>
                                    <div className="flex items-center gap-1.5">
                                        <Label>Client ID</Label>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <InformationCircleIcon className="h-4 w-4 text-zinc-400 cursor-help" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p className="font-medium mb-1">Where to find the Client ID</p>
                                                    <p className="text-xs">Azure Portal → App Registrations → Your App → Overview → Application (client) ID</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <Input autoComplete="new-clientid" name="client-id" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" onChange={(e) => { setEntraCredentials({ ...entraCredentials, client_id: e.target.value }) }}/>
                                </Field>
                                <Field>
                                    <div className="flex items-center gap-1.5">
                                        <Label>Client Secret</Label>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <InformationCircleIcon className="h-4 w-4 text-zinc-400 cursor-help" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p className="font-medium mb-1">Where to find the Client Secret</p>
                                                    <p className="text-xs">Azure Portal → App Registrations → Your App → Certificates & secrets → Client secrets</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <Input autoComplete="new-password" type="password" name="secret" placeholder="Enter client secret" onChange={(e) => { setEntraCredentials({ ...entraCredentials, secret: e.target.value }) }}/>
                                </Field>
                            </>
                        )}

                        {selectedCredentialType !== 'legacy' && requiredPermissions && (
                            <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-4 mt-4 space-y-3">
                                <div>
                                    <Text className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                        Microsoft Graph API Permissions
                                    </Text>
                                    <Text className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                        {requiredPermissions.description}
                                    </Text>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {requiredPermissions.permissions && requiredPermissions.permissions.map((permission, idx) => (
                                        <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded text-xs font-mono font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100">
                                            {permission}
                                        </span>
                                    ))}
                                </div>
                                <Text className="text-xs text-blue-600 dark:text-blue-400">
                                    Your organization's Microsoft Entra admin will need to approve these permissions during the consent flow.
                                </Text>
                            </div>
                        )}
                    </DialogBody>
                    <DialogActions>
                    <Button plain onClick={() => {setIsOpen(false); setSelectedCredentialType(''); setEntraCredentials({});}}>
                        Cancel
                    </Button>
                    <Button color='blue' onClick={() => SaveCredential()} disabled={connectingId !== null}>
                        {connectingId !== null ? 'Connecting...' : (selectedCredentialType === 'legacy' ? 'Save' : 'Save & Connect')}
                    </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={editIsOpen} onClose={()=>{setEditIsOpen(false), setEditedCredential({})}}>
                    <DialogTitle>Edit Credential</DialogTitle>
                    <DialogDescription>
                        You can edit any element of the credential here. Please note that only fields with new values will be updated.
                    </DialogDescription>
                    <DialogBody>
                        <Field>
                            <Label>Credential Name</Label>
                            <Input name="credential-name" value={editedCredential.name} onChange={(e) => { setEditedCredential({ ...editedCredential, name: e.target.value }) }} />
                        </Field>
                        <Field>
                            <Label>Tenant ID</Label>
                            <Input autoComplete="new-tenantid" name="tenant-id" placeholder="Enter new tenant ID to update" onChange={(e) => { setEditedCredential({ ...editedCredential, tenant_id: e.target.value }) }}/>
                        </Field>
                        {(!editedCredential.auth_type || editedCredential.auth_type === 'legacy') && (
                            <>
                                <Field>
                                    <Label>Client ID</Label>
                                    <Input autoComplete="new-clientid" name="client-id" placeholder="Enter new client ID to update" onChange={(e) => { setEditedCredential({ ...editedCredential, client_id: e.target.value }) }}/>
                                </Field>
                                <Field>
                                    <Label>Client Secret</Label>
                                    <Input autoComplete="new-password" type="password" name="secret" placeholder="Enter new secret to update" onChange={(e) => { setEditedCredential({ ...editedCredential, secret: e.target.value }) }}/>
                                </Field>
                            </>
                        )}
                        {editedCredential.auth_type === 'platform_app' && (
                            <div className="rounded-md bg-zinc-100 dark:bg-zinc-800 p-4 mt-4">
                                <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                                    This credential uses the platform app for authentication. Only the name and tenant ID can be edited.
                                </Text>
                            </div>
                        )}
                    </DialogBody>
                    <DialogActions>
                    <Button plain onClick={() => setEditIsOpen(false)}>
                        Cancel
                    </Button>
                    <Button color='blue' onClick={() => UpdateCredential()}>Save</Button>
                    </DialogActions>
                </Dialog>

                <Alert open={deleteIsOpen} onClose={setDeleteIsOpen}>
                    <AlertTitle>Are you sure you want to delete this credential?</AlertTitle>
                    <AlertDescription>
                        You can only delete a credential if its not associated with any integrations. This action cannot be undone.
                    </AlertDescription>
                    {credentialToDelete.auth_type === 'platform_app' && (
                        <AlertDescription>
                            <Strong>Note:</Strong> This does not revoke access in your Microsoft Entra tenant. To fully remove access, you must delete the enterprise application from your Azure Portal.
                        </AlertDescription>
                    )}
                    <AlertDescription>
                        Credential to delete: <Strong>{credentialToDelete.name}</Strong>
                    </AlertDescription>
                    <AlertActions>
                    <Button plain onClick={() => setDeleteIsOpen(false)}>
                        Cancel
                    </Button>
                    <Button color='red' onClick={() => DeleteCredential(credentialToDelete)}>Delete</Button>
                    </AlertActions>
                </Alert>

                <Alert open={disconnectIsOpen} onClose={setDisconnectIsOpen}>
                    <AlertTitle>Disconnect this credential?</AlertTitle>
                    <AlertDescription>
                        This will clear the connection status and cached tokens on our side. You can reconnect at any time by granting admin consent again.
                    </AlertDescription>
                    <AlertDescription>
                        <Strong>Note:</Strong> This does not revoke access in your Microsoft Entra tenant. To fully remove access, you must delete the enterprise application from your Azure Portal.
                    </AlertDescription>
                    <AlertDescription>
                        Credential to disconnect: <Strong>{credentialToDisconnect.name}</Strong>
                    </AlertDescription>
                    <AlertActions>
                    <Button plain onClick={() => {setDisconnectIsOpen(false); setCredentialToDisconnect({})}}>
                        Cancel
                    </Button>
                    <Button color='amber' onClick={() => disconnectEntra()}>Disconnect</Button>
                    </AlertActions>
                </Alert>

                {/* Connecting Overlay Dialog */}
                <Dialog open={connectingId !== null} onClose={() => {}}>
                    <div className="flex flex-col items-center justify-center py-8">
                        <svg className="animate-spin h-12 w-12 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-900 dark:text-white font-medium text-lg">Connecting to Microsoft Entra...</p>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">Please complete authorization in the popup window</p>
                        <p className="text-gray-400 dark:text-gray-500 text-xs mt-4">If the popup doesn't appear, check your popup blocker</p>
                    </div>
                </Dialog>
            </AdminSidebar>
        </>
    );   
}

